import { useEffect, useRef, useState } from 'react';
import { Check, Link2 } from 'lucide-react';
import type { Task } from '../../lib/tasksApi';
import { copyText } from '../../lib/clipboard';
import { taskShareUrl } from '../../lib/taskLink';
import { useI18n } from '../../contexts/I18nContext';

/**
 * Icon button that copies a task's shareable link and acknowledges the click
 * with a check mark for a moment. Used on the detail page header and in the
 * hover preview card, so a link can be grabbed from the calendar without
 * opening the task (Andrei, 2026-10-01).
 *
 * The click's outcome is also reported through `onCopied` / `onFailed` so a
 * host with a toast (the detail page) can say it in words; the preview card
 * has no toast provider and relies on the icon alone.
 */
export function CopyTaskLinkButton({
  task,
  size = 16,
  className,
  onCopied,
  onFailed,
}: {
  task: Pick<Task, 'id' | 'identifier'>;
  size?: number;
  className?: string;
  onCopied?: () => void;
  onFailed?: () => void;
}) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const reset = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(reset.current), []);

  async function copy() {
    if (await copyText(taskShareUrl(task))) {
      setCopied(true);
      onCopied?.();
      window.clearTimeout(reset.current);
      reset.current = window.setTimeout(() => setCopied(false), 2000);
    } else {
      onFailed?.();
    }
  }

  const label = copied ? t('tasks.share.copied') : t('tasks.share.copyLink');
  return (
    <button
      type="button"
      onClick={() => void copy()}
      aria-label={label}
      title={label}
      className={
        className ??
        'flex h-7 w-7 items-center justify-center rounded text-gray-500 hover:bg-gray-200 hover:text-gray-800'
      }
    >
      {copied ? <Check size={size} className="text-emerald-500" /> : <Link2 size={size} />}
    </button>
  );
}
